/**
 * Aba Tema & cores (C4) — fiel ao mockup (.theme-cards / .accent-row / .seg /
 * .switch). Usa o ThemeContext estendido: tema (claro/escuro/sistema),
 * cor de destaque, densidade de tabela e animações. Tudo persiste no navegador.
 */
import React, { useState } from "react";

import { useThemeToggle } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";

const TEMAS = [
  { id: "light", label: "Claro", dot: "#FB7185", bars: 3 },
  { id: "dark", label: "Escuro", dot: "#34D399", bars: 3 },
  { id: "system", label: "Sistema", dot: "#FBBF24", bars: 2 },
];

const DENSIDADES = [
  { id: "compact", label: "Compacto" },
  { id: "cozy", label: "Equilibrado" },
  { id: "comfort", label: "Confortável" },
];

function Preview({ variant, dot, bars }) {
  return (
    <div className={`theme-card__preview ${variant}`}>
      <div className="pv-side">
        <div className="pv-dot" style={{ background: dot }} />
        <div className="pv-bar" />
        <div className="pv-bar" style={{ width: "70%" }} />
        {bars > 2 ? <div className="pv-bar" style={{ width: "85%" }} /> : null}
      </div>
      <div className="pv-main">
        <div className="pv-row" style={{ width: "80%" }} />
        <div className="pv-row" />
        <div className="pv-row" style={{ width: "60%" }} />
      </div>
    </div>
  );
}

export default function AparenciaTab() {
  const {
    themeMode,
    setThemeMode,
    accentColor,
    setAccentColor,
    accents,
    densidade,
    setDensidade,
    animacoes,
    setAnimacoes,
  } = useThemeToggle();
  const toast = useToast();

  const accentAtual = accents.find((a) => a.id === accentColor);

  // As mudanças aplicam ao vivo (preview global + persistem no navegador). O
  // botão "Salvar" confirma a escolha e fica habilitado só quando há alteração
  // desde o último salvar/abrir. (Snapshot capturado na montagem.)
  const atual = { themeMode, accentColor, densidade, animacoes };
  const [salvo, setSalvo] = useState(atual);
  const alterado = JSON.stringify(salvo) !== JSON.stringify(atual);

  function salvarAlteracoes() {
    setSalvo(atual);
    // PREF-UI (2026-08-04): a aparencia agora vai para o perfil do usuario no
    // servidor (SincronizadorPreferencias grava logo apos a mudanca), entao a
    // escolha acompanha a pessoa em qualquer dispositivo. A mensagem antiga
    // dizia "neste navegador" e descrevia com precisao o bug: o usuario perdia
    // a cor ao trocar de maquina e ate ao sair e entrar de novo.
    toast.success("Aparência salva na sua conta.");
  }

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Tema &amp; cores</h2>
        <p className="set-section__desc">Escolha a aparência do sistema. Vale só para você, em qualquer dispositivo.</p>
      </div>
      <div className="set-section__body">
        {/* Tema */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Tema</div>
            <div className="set-row__desc">Claro, escuro ou seguir as preferências do sistema.</div>
          </div>
          <div className="set-row__control">
            <div className="theme-cards">
              {TEMAS.map((t) => (
                <div
                  key={t.id}
                  className={`theme-card ${themeMode === t.id ? "is-on" : ""}`}
                  role="button"
                  aria-pressed={themeMode === t.id}
                  aria-label={`Tema ${t.label}`}
                  tabIndex={0}
                  onClick={() => setThemeMode(t.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setThemeMode(t.id); } }}
                >
                  <Preview variant={t.id} dot={t.dot} bars={t.bars} />
                  <div className="theme-card__label"><span className="theme-card__radio" /> {t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cor de destaque */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Cor de destaque</div>
            <div className="set-row__desc">Define a cor de botões, links e elementos em destaque.</div>
          </div>
          <div className="set-row__control">
            <div className="accent-row">
              {accents.map((a) => (
                <div
                  key={a.id}
                  className={`accent-sw ${accentColor === a.id ? "is-on" : ""}`}
                  data-c={a.id}
                  role="button"
                  aria-pressed={accentColor === a.id}
                  aria-label={`Cor ${a.label}`}
                  tabIndex={0}
                  title={a.label}
                  onClick={() => setAccentColor(a.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAccentColor(a.id); } }}
                >
                  <span className="ring" />
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>
                </div>
              ))}
              <span className="accent-hex">
                {accentAtual ? `${accentAtual.label} · ${accentAtual.swatch}` : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Densidade */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Densidade das tabelas</div>
            <div className="set-row__desc">Quanta informação cabe por tela.</div>
          </div>
          <div className="set-row__control">
            <div className="seg three" style={{ maxWidth: 300 }}>
              {DENSIDADES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={densidade === d.id ? "is-on" : ""}
                  onClick={() => setDensidade(d.id)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Animações */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Animações da interface</div>
            <div className="set-row__desc">Transições e micro-interações ao navegar.</div>
          </div>
          <div className="set-row__control" style={{ alignItems: "flex-start" }}>
            <div
              className={`switch ${animacoes ? "is-on" : ""}`}
              role="switch"
              aria-checked={animacoes}
              tabIndex={0}
              onClick={() => setAnimacoes(!animacoes)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAnimacoes(!animacoes); } }}
            />
          </div>
        </div>

      </div>

      <div className="set-section__foot">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!alterado}
          onClick={salvarAlteracoes}
        >
          {alterado ? "Salvar alterações" : "Salvo"}
        </button>
      </div>
    </section>
  );
}
